import { Button, Modal, Spinner } from '@salesforce/design-system-react';

import { IconCategory } from '../../../shared/types/Enum';

/**
 * Reusable Confirmation Modal Component
 * Replaces window.confirm() calls with a modal dialog
 */
interface ConfirmationModalProps {
  cancelText?: string;
  children: React.ReactNode;
  // Shows a spinner inside the confirm button (and disables both buttons) while an async
  // confirm action is in flight.
  confirmLoading?: boolean;
  confirmText?: string;
  onCancel: () => void;
  onConfirm: () => void;
  opened: boolean;
  title: string;
}

/**
 * Built on SLDS `Modal`'s own `prompt="warning"` variant — the genuine Cosmos
 * destructive-confirmation look (a `slds-theme_warning`/alert-texture header plus
 * `slds-modal_prompt` sizing), not a hand-styled header — with `variant="destructive"` on the
 * confirm button (a real semantic `Button` variant) instead of a hardcoded color.
 */
export const ConfirmationModal = ({
  cancelText = 'Cancel',
  children,
  confirmLoading = false,
  confirmText = 'Confirm',
  onCancel,
  onConfirm,
  opened,
  title,
}: ConfirmationModalProps) => (
  <Modal
    footer={[
      <Button
        disabled={confirmLoading}
        iconCategory={IconCategory.Utility}
        iconName="close"
        iconPosition="left"
        key="cancel"
        label={cancelText}
        onClick={onCancel}
        variant="neutral"
      />,
      <Button disabled={confirmLoading} key="confirm" onClick={onConfirm} variant="destructive">
        {confirmLoading ? (
          <Spinner
            containerStyle={{ marginInlineEnd: 'var(--slds-g-spacing-2)' }}
            hasContainer={false}
            isInline
            size="x-small"
          />
        ) : null}
        {confirmText}
      </Button>,
    ]}
    heading={title}
    isOpen={opened}
    onRequestClose={onCancel}
    prompt="warning"
  >
    {children}
  </Modal>
);
